import { Colorize, People, RecordVoiceOver } from '@material-ui/icons';
import {
  IconCalendarStats,
  IconPillOff,
  IconPill,
  IconBuildingHospital,
} from '@tabler/icons-react';
import { format, isValid } from 'date-fns';
import React, { useEffect } from 'react';

import Fyi from 'src/components/general/PatientMenu/highlights/Fyi';
import { VisitReason } from 'src/dropInClinic/visitReason/VisitReason';
import { OBSERVABILITY_MY_DAY_TODAY, UPDATED_MDT_INTAKE_CARD } from 'src/featureFlags/currentFlags';
import { useFeatureFlag } from 'src/featureFlags/useFeatureFlag';
import { IntakePrepResponsibility } from 'src/generated/gql/graphql';
import AcceptPopInClinicSuggestionButton from 'src/myDayToday/components/AcceptPopInClinicSuggestionButton';
import { CardDivider } from 'src/myDayToday/components/CardDivider';
import { DrugScreeningStatus } from 'src/myDayToday/components/DrugScreeningStatus';
import { EventTypeLabel } from 'src/myDayToday/components/EventSubType';
import { EventSubTypeIcon } from 'src/myDayToday/components/EventSubTypeIcon';
import { InformationRow } from 'src/myDayToday/components/InformationRow';
import { MedicationRunOutLabel } from 'src/myDayToday/components/MedicationRunOutLabel';
import { PatientName } from 'src/myDayToday/components/PatientName';
import { PopInVisitCardHeader } from 'src/myDayToday/components/PopInVisitCardHeader';
import { useScheduledEventCardStyles } from 'src/myDayToday/components/ScheduledEventCard.styles';
import { StateBadge } from 'src/myDayToday/components/StateBadge';
import { VisitTimeFrame } from 'src/myDayToday/components/VisitTimeFrame';
import { EventTiming } from 'src/myDayToday/components/getScheduledEventTiming';
import { useProviderId } from 'src/myDayToday/domain/useProviderId';
import { EventCardPatient } from 'src/myDayToday/types/EventCardPatient';
import { useObservability } from 'src/observability/useObservability';

type PopInVisitCardProps = {
  patient: EventCardPatient;
  requestId: string;
  visitReason?: string;
  visitType: string;
  currentTime: Date;
  scheduledStartTime: Date;
  expectedDuration: number;
  reasonDetails?: Record<string, any>;
  hasBeenSufficientlyNotified: boolean;
  prepResponsibility?: IntakePrepResponsibility;
  onScheduleRefresh: () => void;
};

const IntakeInformationRows = ({ patient }: { patient: PopInVisitCardProps['patient'] }) => {
  const showBupeExperience = patient.primaryConcernCheck.some(primaryConcern =>
    /opioid/i.test(primaryConcern),
  );
  const showAlcoholTreatmentExperience = patient.primaryConcernCheck.some(primaryConcern =>
    /alcohol/i.test(primaryConcern),
  );

  return (
    <>
      <InformationRow
        icon={<RecordVoiceOver />}
        label="Primary concern(s)"
        value={patient.primaryConcernCheck.join(', ') || 'None'}
      />
      {showBupeExperience && (
        <InformationRow
          icon={<IconPill />}
          label="Bupe experience"
          value={patient.bupeFamiliarity}
        />
      )}
      {showAlcoholTreatmentExperience && (
        <InformationRow
          icon={<IconBuildingHospital />}
          label="Prior AUD treatment"
          value={patient.alcoholTreatmentExperience}
        />
      )}
    </>
  );
};

const GeneralInformationRows = ({
  patient,
  reason,
}: {
  patient: PopInVisitCardProps['patient'];
  reason: React.ReactNode | null;
}) => {
  return (
    <>
      <InformationRow icon={<RecordVoiceOver />} label="Visit reason" value={reason} />
      <InformationRow
        icon={<IconCalendarStats />}
        label="Visit cadence"
        value={patient.visitCadence}
      />
      <InformationRow
        icon={<IconPillOff />}
        label="Bupe runout date"
        value={<MedicationRunOutLabel runOutDate={patient.medicationRunOutDate} />}
      />
      <InformationRow
        icon={<Colorize />}
        label="Drug screening status"
        value={
          <DrugScreeningStatus screeningStatus={patient.drugScreeningStatus} defaultLabel="--" />
        }
      />
      <InformationRow
        icon={<People />}
        label="Last visit"
        value={
          // Note for 'as Date': isValid allows us to be sure this is a Date
          isValid(patient.lastVisit) ? format(patient.lastVisit as Date, 'M/d/yyyy') : null
        }
      />
    </>
  );
};

const PopInVisitCard = (props: PopInVisitCardProps) => {
  const classes = useScheduledEventCardStyles();

  const enableObservability = useFeatureFlag(OBSERVABILITY_MY_DAY_TODAY);
  const enableIntakeSpecificCard = useFeatureFlag(UPDATED_MDT_INTAKE_CARD);
  const sendStaffEvent = useObservability(enableObservability);
  const providerId = useProviderId();

  const containerClasses = [classes.container, EventTiming.Imminent];

  let reason: React.ReactNode | null = null;
  if (props.visitReason) {
    reason = props.visitReason;
  } else if (props.reasonDetails) {
    reason = <VisitReason visitReasonDetails={props.reasonDetails} />;
  }

  useEffect(() => {
    sendStaffEvent({
      eventType: 'myDayToday:popInVisitCardViewed',
      userId: providerId ?? null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const InformationRows =
    props.visitType.includes('intake') && enableIntakeSpecificCard ? (
      <IntakeInformationRows patient={props.patient} />
    ) : (
      <GeneralInformationRows patient={props.patient} reason={reason} />
    );

  return (
    <div className={containerClasses.join(' ')} data-testid="pop-in-clinic-card">
      <div className="header">
        <PopInVisitCardHeader />
      </div>
      <div className="body">
        <div className="avatar">
          <img src={props.patient.avatarUrl} />
          <div className="patientInformation">
            <StateBadge city={props.patient.city} state={props.patient.state} />
            <Fyi patientId={props.patient.patientId} />
          </div>
        </div>
        <div className="details">
          <div className="visitType">
            <EventSubTypeIcon subType={props.visitType} size={18} />
            <EventTypeLabel
              subType={props.visitType}
              prepResponsibility={props.prepResponsibility}
            />
          </div>
          <div className="patientName">
            <PatientName {...props.patient} />
          </div>
          <VisitTimeFrame start={props.scheduledStartTime} duration={props.expectedDuration} />

          <CardDivider />

          <div className="informationList">{InformationRows}</div>

          <CardDivider />

          <AcceptPopInClinicSuggestionButton
            requestId={props.requestId}
            disabled={!props.hasBeenSufficientlyNotified}
            onScheduleRefresh={props.onScheduleRefresh}
            prepResponsibility={props.prepResponsibility}
          />
        </div>
      </div>
    </div>
  );
};

export default PopInVisitCard;
